// frontend/src/components/Register.jsx
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config/api";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Calendar,
  CreditCard,
  Phone,
  MapPin,
  Home,
  Building2,
  FileText,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

// ============================================
// COMPONENTE DE PROGRESO
// ============================================
const ProgressSteps = ({ passoAtual }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
            passoAtual === 1
              ? "bg-[#449779] text-white"
              : "bg-gray-200 text-[#449779]"
          }`}
        >
          1
        </div>
        <div
          className={`ml-2 text-sm font-medium transition-all duration-300 ${
            passoAtual === 1 ? "text-[#449779]" : "text-gray-500"
          }`}
        >
          Dados Básicos
        </div>
      </div>

      <div className="flex-1 h-1 mx-4 bg-gray-200">
        <div
          className="h-full bg-[#449779] transition-all duration-500"
          style={{ width: passoAtual >= 2 ? "100%" : "0%" }}
        />
      </div>

      <div className="flex items-center">
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
            passoAtual === 2
              ? "bg-[#449779] text-white"
              : "bg-gray-200 text-[#449779]"
          }`}
        >
          2
        </div>
        <div
          className={`ml-2 text-sm font-medium transition-all duration-300 ${
            passoAtual === 2 ? "text-[#449779]" : "text-gray-500"
          }`}
        >
          Perfil e Endereço
        </div>
      </div>
    </div>
  </div>
);

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function Registro() {
  const { login98 } = useAuth();
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [verificandoExistencia, setVerificandoExistencia] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const [passoAtual, setPassoAtual] = useState(1);
  const [cepHabilitado, setCepHabilitado] = useState(null);
  const [condominioInfo, setCondominioInfo] = useState(null);
  const [mostrarFormularioContacto, setMostrarFormularioContacto] =
    useState(false);
  const [contactoEmergencia, setContactoEmergencia] = useState({
    nome: "",
    telefone: "",
    email: "",
  });

  // Aceitação dos termos
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptDataShare, setAcceptDataShare] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);

  const [formData, setFormData] = useState({
    cpf: "",
    nome: "",
    apelido: "",
    email: "",
    confirmarEmail: "",
    phone: "",
    senha: "",
    confirmarSenha: "",
    tipo_residente: "",
    comprovante_residencia: null,
    endereco: {
      cep: "",
      uf: "",
      cidade: "",
      bairro: "",
      logradouro: "",
      numero: "",
      complemento: "",
      condominio: "",
      referencia: "",
      semNumero: false,
      addressEdited: false,
    },
  });

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [campoObrigatorio, setCampoObrigatorio] = useState({});
  const [errosExistencia, setErrosExistencia] = useState({
    cpf: "",
    email: "",
  });

  const [showProcessInfo, setShowProcessInfo] = useState(false);

  const timeoutRefCPF = useRef(null);
  const timeoutRefEmail = useRef(null);
  const processInfoTimeoutRef = useRef(null);
  const cepTimeoutRef = useRef(null);

  // Condomínios predefinidos
  const condominios = [
    {
      nome: "Gran Ville São Venâncio",
      faixas: [{ inicio: 13296354, fim: 13296374 }],
      bairro: "Gran Ville São Venâncio",
      logradouro: "",
    },
    {
      nome: "Bela Vista",
      faixas: [{ inicio: 13295850, fim: 13295853 }],
      bairro: "Bela Vista",
      logradouro: "Rua 1",
    },
    {
      nome: "Residencial Pecan",
      faixas: [{ inicio: 13296432, fim: 13296441 }],
      bairro: "Residencial Pecan",
      logradouro: "",
    },
    {
      nome: "Condomínio Terras da Alvorada",
      faixas: [{ inicio: 13296507, fim: 13296537 }],
      bairro: "Condomínio Terras da Alvorada",
      logradouro: "Rua Camille Pissarro",
    },
  ];

  // 🔥 VALIDACIÓN DE CONTRASEÑA - MÍNIMO 8 CARACTERES
  const passwordValidation = {
    minLength: formData.senha.length >= 8,
    hasUpperCase: /[A-Z]/.test(formData.senha),
    hasLowerCase: /[a-z]/.test(formData.senha),
    hasNumber: /[0-9]/.test(formData.senha),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.senha),
  };

  const isPasswordValid =
    passwordValidation.minLength &&
    passwordValidation.hasUpperCase &&
    passwordValidation.hasLowerCase &&
    passwordValidation.hasNumber;

  // Helpers
  const cepRegex = /^\d{5}-?\d{3}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const normalizeCEP = (cep) =>
    cep.replace(/\D/g, "").replace(/^(\d{5})(\d{3})$/, "$1-$2");
  const formatCPF = (cpf) =>
    cpf
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

  const validarCPF = (cpf) => {
    cpf = cpf.replace(/\D/g, "");
    if (cpf.length !== 11) return false;
    if (/^(\d)\1+$/.test(cpf)) return false;
    let soma = 0,
      resto;
    for (let i = 1; i <= 9; i++)
      soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.substring(9, 10))) return false;
    soma = 0;
    for (let i = 1; i <= 10; i++)
      soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    return resto === parseInt(cpf.substring(10, 11));
  };

  const validarEmail = (email) => emailRegex.test(email);

  const verificarCEPHabilitado = (cep) => {
    const cepNumerico = parseInt(cep.replace(/\D/g, ""));
    if (isNaN(cepNumerico) || cepNumerico.toString().length !== 8) {
      return { habilitado: false, condominio: null, info: null };
    }
    for (const condominio of condominios) {
      for (const faixa of condominio.faixas) {
        if (cepNumerico >= faixa.inicio && cepNumerico <= faixa.fim) {
          return {
            habilitado: true,
            condominio: condominio.nome,
            info: condominio,
          };
        }
      }
    }
    return { habilitado: false, condominio: null, info: null };
  };

  const enviarContactoEmergencia = async () => {
    if (
      !contactoEmergencia.nome ||
      !contactoEmergencia.telefone ||
      !contactoEmergencia.email
    ) {
      setErro("⚠️ Por favor, preencha nome, telefone e e-mail para contato.");
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/condominios/interesse`, {
        cep: formData.endereco.cep,
        condominio: "Nao habilitado",
        ...contactoEmergencia,
      });
      setSucesso(
        "✅ Contato registrado! Avisaremos quando seu condomínio estiver disponível."
      );
      setTimeout(() => {
        setMostrarFormularioContacto(false);
        setSucesso("");
        navigate("/");
      }, 3000);
    } catch (error) {
      setErro("⚠️ Erro ao enviar contato. Tente novamente.");
    }
  };

  const verificarCPFExistente = async (cpf) => {
    const cpfConFormato = cpf;
    if (cpfConFormato.length !== 14 || !validarCPF(cpfConFormato)) {
      setErrosExistencia((prev) => ({ ...prev, cpf: "" }));
      return;
    }
    try {
      setVerificandoExistencia(true);
      const response = await axios.post(`${API_BASE_URL}/auth/verificar-cpf`, {
        cpf: cpfConFormato,
      });
      if (response.data.exists) {
        setErrosExistencia((prev) => ({
          ...prev,
          cpf: "CPF já está registrado. Faça login para continuar.",
        }));
      } else {
        setErrosExistencia((prev) => ({ ...prev, cpf: "" }));
      }
    } catch (error) {
      console.error("Erro ao verificar CPF:", error);
      setErrosExistencia((prev) => ({ ...prev, cpf: "" }));
    } finally {
      setVerificandoExistencia(false);
    }
  };

  const verificarEmailExistente = async (email) => {
    if (!validarEmail(email)) {
      setErrosExistencia((prev) => ({ ...prev, email: "" }));
      return;
    }
    try {
      setVerificandoExistencia(true);
      const response = await axios.post(
        `${API_BASE_URL}/auth/verificar-email`,
        { email }
      );
      if (response.data.exists) {
        setErrosExistencia((prev) => ({
          ...prev,
          email: "E-mail já está registrado. Faça login para continuar.",
        }));
      } else {
        setErrosExistencia((prev) => ({ ...prev, email: "" }));
      }
    } catch (error) {
      console.error("Erro ao verificar email:", error);
      setErrosExistencia((prev) => ({ ...prev, email: "" }));
    } finally {
      setVerificandoExistencia(false);
    }
  };

  const buscarCEP = async (cep) => {
    const cleanCEP = cep.replace(/\D/g, "");
    if (cleanCEP.length !== 8) return;
    try {
      setBuscandoCep(true);
      const verificacion = verificarCEPHabilitado(cleanCEP);
      if (!verificacion.habilitado) {
        setCepHabilitado(false);
        setMostrarFormularioContacto(true);
        setErro("Seu condomínio ainda não está habilitado. Deixe seu contato.");
        setFormData((prev) => ({
          ...prev,
          endereco: {
            ...prev.endereco,
            cep: normalizeCEP(cleanCEP),
            condominio: "",
          },
        }));
        setBuscandoCep(false);
        return;
      }
      setCepHabilitado(true);
      setCondominioInfo({ nome: verificacion.condominio });
      setMostrarFormularioContacto(false);
      setErro("");
      const res = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
      const data = await res.json();
      if (data.erro) {
        setErro("⚠️ CEP não encontrado.");
        setBuscandoCep(false);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          cep: normalizeCEP(cleanCEP),
          uf: data.uf || "SP",
          cidade: data.localidade || "Itupeva",
          bairro: verificacion.info?.bairro || data.bairro || "",
          logradouro: verificacion.info?.logradouro || data.logradouro || "",
          condominio: verificacion.condominio,
        },
      }));
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      setErro("⚠️ Erro ao buscar CEP. Tente novamente.");
    } finally {
      setBuscandoCep(false);
    }
  };

  useEffect(() => {
    const cleanCEP = formData.endereco.cep.replace(/\D/g, "");
    if (cleanCEP.length === 8) {
      if (cepTimeoutRef.current) clearTimeout(cepTimeoutRef.current);
      cepTimeoutRef.current = setTimeout(
        () => buscarCEP(formData.endereco.cep),
        500
      );
    }
    return () => {
      if (cepTimeoutRef.current) clearTimeout(cepTimeoutRef.current);
    };
  }, [formData.endereco.cep]);

  useEffect(() => {
    if (showProcessInfo) {
      if (processInfoTimeoutRef.current)
        clearTimeout(processInfoTimeoutRef.current);
      processInfoTimeoutRef.current = setTimeout(
        () => setShowProcessInfo(false),
        10000
      );
    }
    return () => {
      if (processInfoTimeoutRef.current)
        clearTimeout(processInfoTimeoutRef.current);
    };
  }, [showProcessInfo]);

  // Scroll automático hacia arriba cuando hay error o sucesso
  useEffect(() => {
    if (erro || sucesso) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [erro, sucesso]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (campoObrigatorio[name])
      setCampoObrigatorio((prev) => ({ ...prev, [name]: false }));

    if (name === "cpf") {
      const formattedCPF = formatCPF(value);
      setFormData((prev) => ({ ...prev, cpf: formattedCPF }));
      if (errosExistencia.cpf)
        setErrosExistencia((prev) => ({ ...prev, cpf: "" }));
      if (timeoutRefCPF.current) clearTimeout(timeoutRefCPF.current);
      timeoutRefCPF.current = setTimeout(
        () => verificarCPFExistente(formattedCPF),
        500
      );
    } else if (name === "email") {
      setFormData((prev) => ({ ...prev, email: value }));
      if (errosExistencia.email)
        setErrosExistencia((prev) => ({ ...prev, email: "" }));
      if (timeoutRefEmail.current) clearTimeout(timeoutRefEmail.current);
      timeoutRefEmail.current = setTimeout(
        () => verificarEmailExistente(value),
        500
      );
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEnderecoChange = (e) => {
    const { name, value } = e.target;
    if (name === "cep" && campoObrigatorio.cep)
      setCampoObrigatorio((prev) => ({ ...prev, cep: false }));
    setFormData((prev) => ({
      ...prev,
      endereco: { ...prev.endereco, [name]: value },
    }));
  };

  // 🔥 HANDLE FILE CHANGE - MODIFICADO PARA 10MB
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      setErro("⚠️ Formato inválido. Use PDF, JPG, JPEG ou PNG.");
      return;
    }
    // 🔥 CAMBIADO DE 2MB A 10MB
    if (file.size > 10 * 1024 * 1024) {
      setErro("⚠️ Arquivo muito grande. Máximo 10MB.");
      return;
    }
    setFormData((prev) => ({ ...prev, comprovante_residencia: file }));
    // Mostrar mensaje de éxito con el nombre del archivo
    setSucesso(`✅ Arquivo selecionado: ${file.name}`);
    setTimeout(() => setSucesso(""), 3000);
  };

  const validarDadosPessoais = () => {
    const errores = {};
    if (!formData.cpf.trim()) errores.cpf = "CPF é obrigatório";
    else if (!validarCPF(formData.cpf)) errores.cpf = "CPF inválido";
    else if (errosExistencia.cpf) errores.cpf = errosExistencia.cpf;

    if (!formData.nome.trim()) errores.nome = "Nome é obrigatório";
    if (!formData.email.trim()) errores.email = "E-mail é obrigatório";
    else if (!validarEmail(formData.email)) errores.email = "E-mail inválido";
    else if (errosExistencia.email) errores.email = errosExistencia.email;

    if (!formData.phone.trim())
      errores.phone = "WhatsApp / Telefone é obrigatório";
    if (!formData.confirmarEmail.trim())
      errores.confirmarEmail = "Confirmação de e-mail é obrigatória";
    else if (formData.email !== formData.confirmarEmail)
      errores.confirmarEmail = "Os e-mails não coincidem";

    // 🔥 VALIDACIÓN DE CONTRASEÑA - MÍNIMO 8 CARACTERES
    if (!formData.senha) errores.senha = "Senha é obrigatória";
    else if (formData.senha.length < 8)
      errores.senha = "Senha deve ter pelo menos 8 caracteres";
    else if (!/[A-Z]/.test(formData.senha))
      errores.senha = "Senha deve ter pelo menos uma letra maiúscula";
    else if (!/[a-z]/.test(formData.senha))
      errores.senha = "Senha deve ter pelo menos uma letra minúscula";
    else if (!/[0-9]/.test(formData.senha))
      errores.senha = "Senha deve ter pelo menos um número";

    if (!formData.confirmarSenha)
      errores.confirmarSenha = "Confirmação de senha é obrigatória";
    else if (formData.senha !== formData.confirmarSenha)
      errores.confirmarSenha = "As senhas não coincidem";

    return errores;
  };

  const validarDadosEndereco = () => {
    const errores = {};
    if (!formData.comprovante_residencia)
      errores.comprovante_residencia =
        "Comprovante de residência é obrigatório";
    if (!formData.tipo_residente)
      errores.tipo_residente = "Tipo de residente é obrigatório";
    if (!formData.endereco.cep.trim()) errores.cep = "CEP é obrigatório";
    else if (!cepRegex.test(formData.endereco.cep))
      errores.cep = "CEP inválido";
    if (!formData.endereco.numero || formData.endereco.numero.trim() === "")
      errores.numero = "Número é obrigatório";
    return errores;
  };

  const irParaProximoPasso = () => {
    setErro("");
    const errores = validarDadosPessoais();
    if (errosExistencia.cpf || errosExistencia.email) {
      setErro("Usuário já registrado. Faça login para continuar.");
      return;
    }
    if (Object.keys(errores).length > 0) {
      setCampoObrigatorio((prev) => ({ ...prev, ...errores }));
      setErro(Object.values(errores)[0]);
      return;
    }
    setPassoAtual(2);
  };

  const irParaPassoAnterior = () => {
    setPassoAtual(1);
    setErro("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");
    setLoading(true);

    const erroresEndereco = validarDadosEndereco();
    if (Object.keys(erroresEndereco).length > 0) {
      setCampoObrigatorio((prev) => ({ ...prev, ...erroresEndereco }));
      setErro(Object.values(erroresEndereco)[0]);
      setLoading(false);
      return;
    }
    if (!cepHabilitado) {
      setErro(
        "Seu CEP não está habilitado. Por favor, verifique ou deixe seu contato."
      );
      setLoading(false);
      return;
    }

    // Validar aceitação dos termos legais
    if (!acceptTerms) {
      setErro("Você precisa aceitar os Termos de Uso para continuar.");
      setLoading(false);
      return;
    }
    if (!acceptPrivacy) {
      setErro("Você precisa aceitar a Política de Privacidade para continuar.");
      setLoading(false);
      return;
    }
    if (!acceptDataShare) {
      setErro(
        "Você precisa autorizar o compartilhamento de dados para validação do cadastro."
      );
      setLoading(false);
      return;
    }

    const enderecoFinal = {
      ...formData.endereco,
      numero:
        formData.endereco.numero === "S/N" ? null : formData.endereco.numero,
      semNumero: formData.endereco.numero === "S/N",
    };

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("cpf", formData.cpf);
      formDataToSend.append("name", formData.nome);
      formDataToSend.append("apelido", formData.apelido);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("email_confirmation", formData.confirmarEmail);
      formDataToSend.append("password", formData.senha);
      formDataToSend.append("password_confirmation", formData.confirmarSenha);
      formDataToSend.append("tipo_residente", formData.tipo_residente);
      formDataToSend.append("phone", formData.phone);
      if (formData.comprovante_residencia)
        formDataToSend.append(
          "comprovante_residencia",
          formData.comprovante_residencia
        );
      formDataToSend.append("endereco[cep]", enderecoFinal.cep);
      formDataToSend.append("endereco[uf]", enderecoFinal.uf);
      formDataToSend.append("endereco[cidade]", enderecoFinal.cidade);
      formDataToSend.append("endereco[bairro]", enderecoFinal.bairro);
      formDataToSend.append("endereco[logradouro]", enderecoFinal.logradouro);
      formDataToSend.append("endereco[numero]", enderecoFinal.numero || "");
      formDataToSend.append(
        "endereco[complemento]",
        enderecoFinal.complemento || ""
      );
      formDataToSend.append(
        "endereco[condominio]",
        enderecoFinal.condominio || ""
      );
      formDataToSend.append(
        "endereco[referencia]",
        enderecoFinal.referencia || ""
      );
      formDataToSend.append(
        "endereco[semNumero]",
        enderecoFinal.semNumero ? "1" : "0"
      );

      // Consentimentos LGPD
      formDataToSend.append("accept_terms", acceptTerms);
      formDataToSend.append("accept_privacy", acceptPrivacy);
      formDataToSend.append("accept_data_share", acceptDataShare);
      formDataToSend.append("accept_marketing", acceptMarketing);

      await axios.post(`${API_BASE_URL}/auth/register`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSucesso(
        <div>
          <p className="font-bold text-lg mb-2">
            ✅ Cadastro realizado com sucesso!
          </p>
          <p className="mb-2">
            Seu cadastro foi enviado para análise do administrador.
          </p>
          <div className="bg-green-50 p-3 rounded mt-2">
            <p className="text-sm font-medium">O que acontece agora?</p>
            <ul className="text-sm mt-1 space-y-1">
              <li>• Verificaremos seus dados e comprovante</li>
              <li>• Você receberá um e-mail quando for aprovado</li>
              <li>• Após aprovação, poderá fazer login normalmente</li>
            </ul>
          </div>
        </div>
      );
      setTimeout(() => navigate("/login"), 10000);
    } catch (error) {
      let errorMessage = "Erro no servidor. Tente novamente.";
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = [];
        if (errors.cpf) errorMessages.push(errors.cpf[0]);
        if (errors.email) errorMessages.push(errors.email[0]);
        if (errorMessages.length > 0) errorMessage = errorMessages.join(", ");
      }
      setErro(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRefCPF.current) clearTimeout(timeoutRefCPF.current);
      if (timeoutRefEmail.current) clearTimeout(timeoutRefEmail.current);
      if (processInfoTimeoutRef.current)
        clearTimeout(processInfoTimeoutRef.current);
      if (cepTimeoutRef.current) clearTimeout(cepTimeoutRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-100 flex flex-col">
      {/* Header com logo */}
      <div className="pt-8 pb-4 text-center">
        <div className="flex justify-center mb-4">
          <img
            src={`${process.env.PUBLIC_URL}/brecho.png`}
            alt="Brechó Vizinho"
            className="w-16 h-16 object-contain"
          />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Crie sua conta</h1>
        <p className="text-sm text-gray-500 mt-1 px-4">
          Informe seus dados para começar a usar o Brechó Vizinho
        </p>
      </div>

      <div className="flex-grow px-4 pb-8">
        <div className="max-w-md mx-auto">
          <ProgressSteps passoAtual={passoAtual} />

          {/* Botão informações */}
          <div className="text-center mb-4">
            <button
              type="button"
              onClick={() => setShowProcessInfo(!showProcessInfo)}
              className="text-sm text-[#449779] hover:underline"
            >
              {showProcessInfo ? "Ocultar" : "Ver"} como funciona a aprovação
            </button>
          </div>

          {showProcessInfo && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-800">
                    Como funciona o processo de aprovação?
                  </h4>
                  <ul className="mt-2 text-sm text-blue-700 space-y-1">
                    <li>✅ 1. Seu cadastro será enviado para análise</li>
                    <li>✅ 2. Verificaremos seus dados e comprovante</li>
                    <li>✅ 3. Você receberá um e-mail quando for aprovado</li>
                    <li>
                      ✅ 4. Após aprovação, poderá fazer login normalmente
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {erro && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
              <div className="flex items-center justify-between">
                <span className="text-sm">{erro}</span>
                <button onClick={() => setErro("")} className="text-red-500">
                  ✕
                </button>
              </div>
            </div>
          )}

          {sucesso && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="text-sm">{sucesso}</div>
                <button
                  onClick={() => setSucesso("")}
                  className="text-green-500"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* PASO 1 */}
            {passoAtual === 1 && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome completo <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="nome"
                      placeholder="Seu nome completo"
                      value={formData.nome}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                        campoObrigatorio.nome
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  {campoObrigatorio.nome && (
                    <p className="text-red-500 text-xs mt-1">
                      {campoObrigatorio.nome}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Como você quer ser chamado(a)?
                  </label>
                  <input
                    type="text"
                    name="apelido"
                    placeholder="Exemplo: João S."
                    value={formData.apelido}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779]"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Aparecerá em seu perfil, anúncios e chats.
                  </p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-sm font-medium text-gray-700">
                      CPF <span className="text-red-500">*</span>
                    </label>
                    {verificandoExistencia && (
                      <span className="text-xs text-blue-500">
                        Verificando...
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="cpf"
                      placeholder="000.000.000-00"
                      value={formData.cpf}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                        campoObrigatorio.cpf || errosExistencia.cpf
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  {(campoObrigatorio.cpf || errosExistencia.cpf) && (
                    <p className="text-red-500 text-xs mt-1">
                      {campoObrigatorio.cpf || errosExistencia.cpf}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-sm font-medium text-gray-700">
                      E-mail <span className="text-red-500">*</span>
                    </label>
                    {verificandoExistencia && (
                      <span className="text-xs text-blue-500">
                        Verificando...
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                        campoObrigatorio.email || errosExistencia.email
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  {(campoObrigatorio.email || errosExistencia.email) && (
                    <p className="text-red-500 text-xs mt-1">
                      {campoObrigatorio.email || errosExistencia.email}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Enviaremos um e-mail de confirmação.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar E-mail <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="confirmarEmail"
                      placeholder="Confirme seu e-mail"
                      value={formData.confirmarEmail}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                        campoObrigatorio.confirmarEmail
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  {campoObrigatorio.confirmarEmail && (
                    <p className="text-red-500 text-xs mt-1">
                      {campoObrigatorio.confirmarEmail}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp / Telefone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(11) 99999-9999"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                        campoObrigatorio.phone
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  {campoObrigatorio.phone && (
                    <p className="text-red-500 text-xs mt-1">
                      {campoObrigatorio.phone}
                    </p>
                  )}
                </div>

                {/* 🔥 SENHA - MÍNIMO 8 CARACTERES */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="senha"
                      placeholder="Mínimo 8 caracteres"
                      value={formData.senha}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                        campoObrigatorio.senha
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Eye className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {campoObrigatorio.senha && (
                    <p className="text-red-500 text-xs mt-1">
                      {campoObrigatorio.senha}
                    </p>
                  )}
                  {/* 🔥 REQUISITOS DE SENHA */}
                  <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                    <p
                      className={
                        formData.senha.length >= 8 ? "text-green-600" : ""
                      }
                    >
                      ✓ Mínimo 8 caracteres
                    </p>
                    <p
                      className={
                        /[A-Z]/.test(formData.senha) ? "text-green-600" : ""
                      }
                    >
                      ✓ Pelo menos uma letra maiúscula
                    </p>
                    <p
                      className={
                        /[a-z]/.test(formData.senha) ? "text-green-600" : ""
                      }
                    >
                      ✓ Pelo menos uma letra minúscula
                    </p>
                    <p
                      className={
                        /[0-9]/.test(formData.senha) ? "text-green-600" : ""
                      }
                    >
                      ✓ Pelo menos um número
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar Senha <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmarSenha"
                      placeholder="Digite a senha novamente"
                      value={formData.confirmarSenha}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                        campoObrigatorio.confirmarSenha
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Eye className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {campoObrigatorio.confirmarSenha && (
                    <p className="text-red-500 text-xs mt-1">
                      {campoObrigatorio.confirmarSenha}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={irParaProximoPasso}
                  className="w-full bg-[#449779] text-white py-3 rounded-xl font-semibold hover:bg-[#2d5f4d] transition-colors"
                >
                  Próximo Passo →
                </button>
              </div>
            )}

            {/* PASO 2 */}
            {passoAtual === 2 && (
              <div className="space-y-5">
                <button
                  type="button"
                  onClick={irParaPassoAnterior}
                  className="text-sm text-[#449779] hover:underline flex items-center gap-1"
                >
                  ← Voltar para Dados Básicos
                </button>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CEP <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      name="cep"
                      placeholder="Digite seu CEP (ex: 13296354)"
                      value={formData.endereco.cep}
                      onChange={handleEnderecoChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                        campoObrigatorio.cep
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  {campoObrigatorio.cep && (
                    <p className="text-red-500 text-xs mt-1">
                      {campoObrigatorio.cep}
                    </p>
                  )}
                  {buscandoCep && (
                    <p className="text-xs text-[#449779] mt-1">
                      🔍 Buscando endereço...
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Digite o CEP (8 números) e aguarde, a busca será automática
                  </p>
                </div>

                {cepHabilitado === true && condominioInfo && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <strong>Condomínio habilitado:</strong>{" "}
                      {condominioInfo.nome}
                    </p>
                  </div>
                )}

                {/* Formulario de contacto */}
                {mostrarFormularioContacto && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <h4 className="font-medium text-yellow-800 mb-3">
                      📢 Seu condomínio ainda não está disponível
                    </h4>
                    <p className="text-sm text-yellow-700 mb-3">
                      Deixe seu contato e avisaremos quando sua localidade
                      estiver disponível:
                    </p>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Seu nome completo *"
                        value={contactoEmergencia.nome}
                        onChange={(e) =>
                          setContactoEmergencia({
                            ...contactoEmergencia,
                            nome: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-[#449779]"
                      />
                      <input
                        type="tel"
                        placeholder="WhatsApp / Telefone *"
                        value={contactoEmergencia.telefone}
                        onChange={(e) =>
                          setContactoEmergencia({
                            ...contactoEmergencia,
                            telefone: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-[#449779]"
                      />
                      <input
                        type="email"
                        placeholder="E-mail *"
                        value={contactoEmergencia.email}
                        onChange={(e) =>
                          setContactoEmergencia({
                            ...contactoEmergencia,
                            email: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-[#449779]"
                      />
                      <button
                        type="button"
                        onClick={enviarContactoEmergencia}
                        className="w-full bg-yellow-600 text-white py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                      >
                        Enviar contato
                      </button>
                    </div>
                  </div>
                )}

                {cepHabilitado === true && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Logradouro
                      </label>
                      <div className="relative">
                        <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          name="logradouro"
                          value={formData.endereco.logradouro}
                          onChange={handleEnderecoChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none"
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número <span className="text-red-500">*</span>
                        </label>
                        <input
                          name="numero"
                          placeholder="Número"
                          value={formData.endereco.numero}
                          onChange={handleEnderecoChange}
                          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                            campoObrigatorio.numero
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Complemento
                        </label>
                        <input
                          name="complemento"
                          placeholder="Complemento"
                          value={formData.endereco.complemento}
                          onChange={handleEnderecoChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Condomínio
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          name="condominio"
                          value={formData.endereco.condominio}
                          onChange={handleEnderecoChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none"
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          UF
                        </label>
                        <input
                          name="uf"
                          value={formData.endereco.uf}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cidade
                        </label>
                        <input
                          name="cidade"
                          value={formData.endereco.cidade}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
                          readOnly
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bairro
                      </label>
                      <input
                        name="bairro"
                        value={formData.endereco.bairro}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
                        readOnly
                      />
                    </div>

                    <hr className="my-4" />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Perfil: <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="tipo_residente"
                        value={formData.tipo_residente}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] ${
                          campoObrigatorio.tipo_residente
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      >
                        <option value="">Selecione uma opção</option>
                        <option value="proprietario">Proprietário</option>
                        <option value="morador">Morador</option>
                      </select>
                      {campoObrigatorio.tipo_residente && (
                        <p className="text-red-500 text-xs mt-1">
                          {campoObrigatorio.tipo_residente}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Comprovante de Residência{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="file"
                          onChange={handleFileChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779]"
                        />
                      </div>
                      {/* 🔥 MENSAJE ACTUALIZADO A 10MB */}
                      <p className="text-xs text-gray-400 mt-1">
                        Tamanho máximo: 10MB. Formatos: PDF, JPG, JPEG, PNG
                      </p>
                      {formData.comprovante_residencia && (
                        <p className="text-xs text-green-600 mt-1">
                          ✅ {formData.comprovante_residencia.name}
                        </p>
                      )}
                    </div>

                    {/* Términos Legales - Estilo OLX */}
                    <div className="space-y-3 pt-4 border-t border-gray-200">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptTerms}
                          onChange={(e) => setAcceptTerms(e.target.checked)}
                          className="mt-0.5 w-4 h-4 text-[#449779] rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-600">
                          Li e concordo com os{" "}
                          <a
                            href="/termos"
                            target="_blank"
                            className="text-[#449779] hover:underline"
                          >
                            Termos de Uso
                          </a>{" "}
                          do Brechó Vizinho.
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptPrivacy}
                          onChange={(e) => setAcceptPrivacy(e.target.checked)}
                          className="mt-0.5 w-4 h-4 text-[#449779] rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-600">
                          Li e concordo com a{" "}
                          <a
                            href="/privacidade"
                            target="_blank"
                            className="text-[#449779] hover:underline"
                          >
                            Política de Privacidade
                          </a>{" "}
                          e autorizo o tratamento dos meus dados pessoais
                          conforme a LGPD.
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptDataShare}
                          onChange={(e) => setAcceptDataShare(e.target.checked)}
                          className="mt-0.5 w-4 h-4 text-[#449779] rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-600">
                          Autorizo o compartilhamento dos dados necessários para
                          validação do meu cadastro com o proprietário do
                          imóvel, administrador da plataforma, síndico ou
                          administração do condomínio.
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptMarketing}
                          onChange={(e) => setAcceptMarketing(e.target.checked)}
                          className="mt-0.5 w-4 h-4 text-[#449779] rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-600">
                          Desejo receber novidades, promoções e comunicados da
                          comunidade.
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#449779] text-white py-3 rounded-xl font-semibold hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                    >
                      {loading ? "Enviando cadastro..." : "Finalizar Cadastro"}
                    </button>
                  </>
                )}
              </div>
            )}
          </form>

          <div className="text-center pt-6 mt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Já tem uma conta?{" "}
              <Link
                to="/login"
                className="text-[#449779] font-semibold hover:underline"
              >
                Faça login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}